import { ExternalLink, Mail, Linkedin, MapPin, Building2 } from "lucide-react";
import { useT, useLang } from "@/lib/i18n";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";

const COMPANY = {
  cnpj: "35.983.508/0001-31",
  cnpjUrl: "https://cnpj.biz/35983508000131",
  email: "owners@myratchet.com",
};

const TEAM = [
  {
    name: "Raphael Moral",
    roleKey: "about.role.dev",
    linkedin: "https://www.linkedin.com/in/raphaelmoral",
  },
  {
    name: "Igor Gomes Ferreira",
    roleKey: "about.role.sales",
    linkedin: "https://www.linkedin.com/in/igor-gomes-ferreira",
  },
];

export default function About() {
  const t = useT();
  const { lang } = useLang();

  const heroTitle = {
    pt: "Quem somos",
    en: "Who we are",
    es: "Quiénes somos",
  }[lang];
  const heroLead = {
    pt: "Um time pequeno de engenheiros que cansou de copiar log query e colar relatório. Construímos o Ratchet pra acabar com isso — e pra clientes que pensam igual.",
    en: "A small team of engineers tired of copy-pasting log queries and reports. We built Ratchet to end that — for customers who feel the same.",
    es: "Un equipo pequeño de ingenieros cansado de copiar log queries y pegar reportes. Construimos Ratchet para acabar con eso — para clientes que piensan igual.",
  }[lang];

  return (
    <div className="min-h-screen" data-testid="about-page">
      <MarketingNav />

      <section className="max-w-5xl mx-auto px-6 lg:px-8 pt-24 pb-12">
        <div className="overline mb-4">// {lang === "pt" ? "sobre" : lang === "es" ? "sobre" : "about"}</div>
        <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter mb-6" data-testid="about-title">
          {heroTitle}
        </h1>
        <p className="text-lg text-white/70 max-w-3xl leading-relaxed">{heroLead}</p>
      </section>

      {/* Team */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 pb-16">
        <div className="overline mb-6">
          // {lang === "pt" ? "time" : lang === "es" ? "equipo" : "team"}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {TEAM.map((p) => (
            <a
              key={p.name}
              href={p.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="surface p-6 hover:border-primary/40 transition-colors group"
              data-testid={`about-team-${p.name.split(" ")[0].toLowerCase()}`}
            >
              <div className="overline mb-2">{t(p.roleKey)}</div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display font-black text-2xl tracking-tight mb-1">{p.name}</div>
                  <div className="text-xs font-mono text-white/40 truncate max-w-[260px]">
                    {p.linkedin.replace("https://www.", "")}
                  </div>
                </div>
                <div className="opacity-60 group-hover:opacity-100 transition-opacity">
                  <Linkedin className="w-5 h-5" />
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Company */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 pb-24">
        <div className="overline mb-6">
          // {lang === "pt" ? "empresa" : lang === "es" ? "empresa" : "company"}
        </div>
        <div className="surface p-6 lg:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 text-sm">
            <Row
              icon={<Building2 className="w-4 h-4" />}
              label="CNPJ"
              valueText={COMPANY.cnpj}
              href={COMPANY.cnpjUrl}
              testid="about-cnpj"
              lang={lang}
            />
            <Row
              icon={<Mail className="w-4 h-4" />}
              label={lang === "pt" ? "Email" : lang === "es" ? "Email" : "Email"}
              valueText={COMPANY.email}
              href={`mailto:${COMPANY.email}`}
              testid="about-email"
              lang={lang}
            />
            <Row
              icon={<MapPin className="w-4 h-4" />}
              label={lang === "pt" ? "Sede" : lang === "es" ? "Sede" : "Headquarters"}
              valueText="Brasil"
              testid="about-hq"
            />
            <Row
              icon={<ExternalLink className="w-4 h-4" />}
              label={lang === "pt" ? "Domínio" : lang === "es" ? "Dominio" : "Domain"}
              valueText="myratchet.com"
              href="https://myratchet.com"
              testid="about-domain"
              lang={lang}
            />
          </div>
          <div className="border-t border-white/5 mt-8 pt-6 text-xs text-white/40 font-mono leading-relaxed">
            {lang === "pt" ? (
              <>Empresa registrada no Brasil sob CNPJ <a href={COMPANY.cnpjUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">35.983.508/0001-31</a>. Para questões comerciais, parcerias e suporte:{" "}
                <a href={`mailto:${COMPANY.email}`} className="underline hover:text-white">{COMPANY.email}</a>.</>
            ) : lang === "es" ? (
              <>Empresa registrada en Brasil bajo el CNPJ <a href={COMPANY.cnpjUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">35.983.508/0001-31</a>. Para temas comerciales, alianzas y soporte:{" "}
                <a href={`mailto:${COMPANY.email}`} className="underline hover:text-white">{COMPANY.email}</a>.</>
            ) : (
              <>Brazilian company registered under CNPJ <a href={COMPANY.cnpjUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">35.983.508/0001-31</a>. For business inquiries, partnerships and support:{" "}
                <a href={`mailto:${COMPANY.email}`} className="underline hover:text-white">{COMPANY.email}</a>.</>
            )}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

function Row({ icon, label, valueText, href, testid, lang }) {
  const value = href ? (
    <a
      href={href}
      target={href.startsWith("mailto") ? undefined : "_blank"}
      rel="noopener noreferrer"
      className="text-white hover:text-primary transition-colors inline-flex items-center gap-1.5"
    >
      <span>{valueText}</span>
      {!href.startsWith("mailto") && <ExternalLink className="w-3 h-3 opacity-60" />}
    </a>
  ) : (
    <span className="text-white">{valueText}</span>
  );
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <div className="mt-0.5 text-white/40">{icon}</div>
      <div>
        <div className="overline mb-1">{label}</div>
        <div className="text-sm font-mono">{value}</div>
      </div>
    </div>
  );
}
